/**
 * @file Sidebar.tsx
 * @description Fixed left navigation sidebar with active view indication,
 *              data freshness status, and user profile/sign-out.
 */

import React from "react";
import { Activity, LayoutDashboard, ShieldAlert, Upload, LogOut, User, Users, BookOpen, Stethoscope, UserPlus, FlaskConical, Building2, BarChart3, Receipt, CreditCard, BellRing } from "lucide-react";
import { AppView } from "../../types";
import { useLanguage } from "../../contexts/LanguageContext";
import { getRoleForEmail, ROLE_CONFIGS } from "../../config/roles";

interface SidebarProps {
  activeView: AppView;
  onNavigate: (view: AppView) => void;
  userEmail: string | null;
  onSignOut: () => void;
  conflictCount: number;
  lastReconciled: string | null;
  isMock: boolean;
}

const ALL_NAV_ITEMS: { id: AppView; labelKey: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "overview", labelKey: "nav_overview", icon: LayoutDashboard },
  { id: "data-health", labelKey: "nav_data_health", icon: ShieldAlert },
  { id: "ingest", labelKey: "nav_ingest", icon: Upload },
  { id: "users", labelKey: "nav_users", icon: Users },
  { id: "clinical", labelKey: "nav_clinical", icon: BookOpen },
  { id: "billing", labelKey: "nav_billing", icon: Receipt },
  { id: "doctor-dash", labelKey: "header_doctor_title", icon: Stethoscope },
  { id: "nurse-dash", labelKey: "header_nurse_title", icon: UserPlus },
  { id: "lab-dash", labelKey: "header_lab_title", icon: FlaskConical },
  { id: "desk-dash", labelKey: "header_desk_title", icon: Building2 },
  { id: "alerts-dash", labelKey: "Emergency & Alerts", icon: BellRing },
  { id: "payment-dash", labelKey: "Payment & Settlement", icon: CreditCard },
  { id: "dept-dash", labelKey: "header_dept_title", icon: Users },
  { id: "mgmt-dash", labelKey: "header_mgmt_title", icon: BarChart3 },
];

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function Sidebar({
  activeView,
  onNavigate,
  userEmail,
  onSignOut,
  conflictCount,
  lastReconciled,
  isMock,
}: SidebarProps) {
  const { t } = useLanguage();
  const [jarvisMode, setJarvisMode] = React.useState<"ops" | "doctor">("ops");
  
  const userRole = getRoleForEmail(userEmail);
  const roleConfig = ROLE_CONFIGS[userRole];
  
  // If the user's role is doctor by default, default to doctor mode
  React.useEffect(() => {
    if (roleConfig.role === "doctor") setJarvisMode("doctor");
  }, [roleConfig.role]);
  
  // Filter based on role permissions and selected JARVIS mode
  const navItems = ALL_NAV_ITEMS.filter(item => {
    if (!roleConfig.allowedViews.includes(item.id)) return false;
    
    const isDoctorItem = ["doctor-dash", "patient-intel"].includes(item.id);
    const isOpsItem = ["overview", "data-health", "ingest", "users", "nurse-dash", "lab-dash", "desk-dash", "dept-dash", "mgmt-dash"].includes(item.id);
    
    if (jarvisMode === "doctor" && isOpsItem) return false;
    if (jarvisMode === "ops" && isDoctorItem) return false;
    
    return true; // Clinical reference is shared
  });

  return (
    <>
      {/* ─── DESKTOP SIDEBAR ─── */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col z-40"
        style={{
          background: "linear-gradient(180deg, #071428 0%, #040d1a 100%)",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-teal shadow-glow flex-shrink-0">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-sm leading-tight">HospOps</div>
            <div className="text-teal-400 text-xs">Central Dashboard</div>
          </div>
        </div>

        {/* Mock mode badge */}
        {isMock && (
          <div className="mx-4 mt-3 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
            <span className="text-amber-400 text-xs font-medium">⚠ DEMO MODE — Mock Data</span>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ id, labelKey, icon: Icon }) => {
            const isActive = activeView === id;
            return (
              <button
                key={id}
                id={`nav-${id}`}
                onClick={() => onNavigate(id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group ${
                  isActive
                    ? "bg-gradient-teal text-white shadow-glow"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-gray-500 group-hover:text-teal-400"}`} />
                <span>{id === "payment-dash" ? "Payment & Settlement" : id === "alerts-dash" ? "Emergency & Alerts" : t(labelKey)}</span>
                {/* Conflict badge on Data Health */}
                {id === "data-health" && conflictCount > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-bold ${
                    isActive ? "bg-white/20 text-white" : "bg-red-500/20 text-red-400"
                  }`}>
                    {conflictCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Data freshness */}
        {lastReconciled && (
          <div className="mx-4 mb-3 px-4 py-3 rounded-xl bg-white/3 border border-white/5">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">{t("last_reconciled")}</div>
            <div className="text-teal-400 text-sm font-medium">
              {formatRelativeTime(lastReconciled)}
            </div>
            <div className="text-gray-600 text-xs mt-0.5">
              {new Date(lastReconciled).toLocaleTimeString()}
            </div>
          </div>
        )}

        {/* User / Sign Out */}
        <div className="px-3 pb-4 border-t border-white/5 pt-3">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/3">
            <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-teal-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-medium truncate">
                {userEmail || "Demo User"}
              </div>
              <div className="text-gray-500 text-xs">Operations Lead</div>
            </div>
            <button
              id="sign-out-btn"
              onClick={onSignOut}
              title="Sign Out"
              className="text-gray-600 hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ─── MOBILE BOTTOM NAV ─── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#071428]/95 backdrop-blur-xl border-t border-white/10 px-2 py-2 flex items-center justify-around pb-safe">
        {navItems.map(({ id, labelKey, icon: Icon }) => {
          const isActive = activeView === id;
          return (
            <button
              key={`mobile-nav-${id}`}
              onClick={() => onNavigate(id)}
              className="relative flex flex-col items-center justify-center p-2 min-w-[64px]"
            >
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${isActive ? "bg-teal-500/20 text-teal-400 shadow-glow" : "text-gray-400"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className={`text-[10px] mt-1 font-medium whitespace-nowrap overflow-hidden text-ellipsis w-14 text-center ${isActive ? "text-teal-400" : "text-gray-500"}`}>
                {id === "payment-dash" ? "Payment" : id === "alerts-dash" ? "Alerts" : t(labelKey).split(" ")[0]}
              </span>
              
              {/* Conflict badge dot */}
              {id === "data-health" && conflictCount > 0 && (
                <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-[#071428]" />
              )}
            </button>
          );
        })}
        {/* Sign out mobile */}
        <button
          onClick={onSignOut}
          className="relative flex flex-col items-center justify-center p-2 min-w-[64px]"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full text-gray-400">
            <LogOut className="w-5 h-5" />
          </div>
          <span className="text-[10px] mt-1 font-medium text-gray-500">Sign Out</span>
        </button>
      </nav>
    </>
  );
}
